import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { connectAdapter } from '@/connect/adapter/streamsConnectAdapter';

export interface AmazonConnectCCPContextValue {
  isReady: boolean;
  error: string | null;
}

const AmazonConnectCCPContext =
  createContext<AmazonConnectCCPContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useAmazonConnectReady(): AmazonConnectCCPContextValue {
  const ctx = useContext(AmazonConnectCCPContext);
  return ctx ?? { isReady: false, error: null };
}

interface AmazonConnectCCPProviderProps {
  children?: ReactNode;
}

/**
 * Renders the hidden CCP container and initializes Amazon Connect once.
 * Mount this at app root when the user is logged in so CCP stays initialized
 * across route changes (e.g. /dashboard <-> /team).
 */
export function AmazonConnectCCPProvider({
  children,
}: AmazonConnectCCPProviderProps) {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const unsubscribeRefs = useRef<Array<() => void>>([]);

  useEffect(() => {
    let isMounted = true;
    let initTimer: ReturnType<typeof setTimeout> | undefined;

    const initialize = async () => {
      try {
        const userIdentity = await connectAdapter.getUserIdentity();
        if (!userIdentity) {
          if (isMounted) {
            setError('Please sign in with Microsoft first');
            setIsReady(true);
          }
          return;
        }

        initTimer = setTimeout(async () => {
          if (!isMounted) return;

          try {
            setError(null);

            let attempts = 0;
            while (!containerRef.current && attempts < 10 && isMounted) {
              await new Promise(resolve => setTimeout(resolve, 100));
              attempts++;
            }

            if (!containerRef.current || !isMounted) {
              throw new Error('Container element not found');
            }

            const containerElement = containerRef.current;

            await new Promise(resolve => {
              requestAnimationFrame(() => {
                setTimeout(resolve, 50);
              });
            });

            if (!containerElement.parentElement || !isMounted) {
              throw new Error('Container element is not attached to the DOM');
            }

            const existingIframe = containerElement.querySelector('iframe');
            if (existingIframe) {
              containerElement.removeChild(existingIframe);
            }
            connectAdapter.resetSession();
            await connectAdapter.initCCP(containerElement, {
              loginPopup: true,
              loginOptions: {
                autoClose: true,
              },
              softphone: {
                allowFramedSoftphone: true,
              },
            });

            if (!isMounted) return;

            const unsubscribes: Array<() => void> = [];

            const unsubscribeInitialized = connectAdapter.onReady(() => {
              if (!isMounted) return;

              const iframe = containerElement.querySelector('iframe');
              if (iframe) {
                iframe.style.height = '600px';
                iframe.style.width = '100%';
              }

              setError(null);
              setIsReady(true);

              const agentUnsubscribe = connectAdapter.onAgent(() => {
                if (isMounted) {
                  setError(null);
                  setIsReady(true);
                }
              });
              unsubscribes.push(agentUnsubscribe);
            });
            unsubscribes.push(unsubscribeInitialized);

            const unsubscribeAuthFail = connectAdapter.onAuthFail(() => {
              if (isMounted) {
                setError('Authentication failed. Please try again.');
                setIsReady(true);
              }
            });
            unsubscribes.push(unsubscribeAuthFail);

            const unsubscribeRetriesExhausted =
              connectAdapter.onAuthRetriesExhausted(() => {
                if (isMounted) {
                  setError(
                    'Failed to authorize after multiple attempts. Please try again.'
                  );
                  setIsReady(true);
                }
              });
            unsubscribes.push(unsubscribeRetriesExhausted);

            unsubscribeRefs.current = unsubscribes;
          } catch (err) {
            if (isMounted) {
              setError(
                err instanceof Error
                  ? err.message
                  : 'Failed to initialize Amazon Connect'
              );
              setIsReady(true);
            }
          }
        }, 500);
      } catch (err) {
        if (isMounted) {
          setError(
            err instanceof Error
              ? err.message
              : 'Failed to verify authentication'
          );
          setIsReady(true);
        }
      }
    };

    initialize();

    return () => {
      isMounted = false;
      if (initTimer) {
        clearTimeout(initTimer);
      }
      unsubscribeRefs.current.forEach(unsubscribe => {
        try {
          unsubscribe();
        } catch {
          // Ignore cleanup errors
        }
      });
      unsubscribeRefs.current = [];
    };
  }, []);

  const contextValue: AmazonConnectCCPContextValue = { isReady, error };

  return (
    <AmazonConnectCCPContext.Provider value={contextValue}>
      <div
        id="connect-ccp"
        ref={containerRef}
        style={{
          display: 'none',
          width: '1px',
          height: '1px',
          position: 'absolute',
          left: '-9999px',
        }}
      />
      {children}
    </AmazonConnectCCPContext.Provider>
  );
}
